/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { TrmUserStatus } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TrmUserProps {
  id: string;
  personId: string | null;
  emailAddress: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  userStatus: TrmUserStatus;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TrmUserRecord extends Record<TrmUserProps>({
  id: null as any,
  personId: null as any,
  emailAddress: null as any,
  firstName: null as any,
  lastName: null as any,
  phoneNumber: null as any,
  userStatus: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    personId: string | null;
    emailAddress: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    userStatus: TrmUserStatus;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TrmUserRecord {
    if (json == null) {
      throw new Error('TrmUserRecord.fromJS: parameter cannot be null');
    }
    return new TrmUserRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TrmUserJson.id',
      ),
      personId: json.personId,
      emailAddress: getFromOrThrow(json, (j) => j.emailAddress, 'TrmUserJson.emailAddress'),
      firstName: getFromOrThrow(json, (j) => j.firstName, 'TrmUserJson.firstName'),
      lastName: getFromOrThrow(json, (j) => j.lastName, 'TrmUserJson.lastName'),
      phoneNumber: getFromOrThrow(json, (j) => j.phoneNumber, 'TrmUserJson.phoneNumber'),
      userStatus: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'userStatus', TrmUserStatus.Unverified),
        'TrmUserJson.userStatus',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'TrmUserJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    personId: string | null;
    emailAddress: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    userStatus: TrmUserStatus;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TrmUserRecord, Error[]] {
    if (json == null) {
      throw new Error('TrmUserRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TrmUserRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TrmUserJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      personId: tryOrDefault(
        () => json.personId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      emailAddress: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.emailAddress, 'TrmUserJson.emailAddress'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      firstName: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.firstName, 'TrmUserJson.firstName'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastName: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.lastName, 'TrmUserJson.lastName'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      phoneNumber: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.phoneNumber, 'TrmUserJson.phoneNumber'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      userStatus: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'userStatus', TrmUserStatus.Unverified),
            'TrmUserJson.userStatus',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'TrmUserJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TrmUserProps) {
    super(props);
    if (props == null) {
      throw new Error('TrmUserRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TrmUserViewModelBase implements TrmUserProps {
  protected readonly _props: TrmUserProps;
  private _json?: TrmUserProps;

  get id(): string {
    return this._props.id;
  }
  get personId(): string | null {
    return this._props.personId;
  }
  get emailAddress(): string {
    return this._props.emailAddress;
  }
  get firstName(): string {
    return this._props.firstName;
  }
  get lastName(): string {
    return this._props.lastName;
  }
  get phoneNumber(): string {
    return this._props.phoneNumber;
  }
  get userStatus(): TrmUserStatus {
    return this._props.userStatus;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isApproved() {
    return this._props.userStatus === TrmUserStatus.Approved;
  }
  public get isDeactivated() {
    return this._props.userStatus === TrmUserStatus.Deactivated;
  }
  public get isDenied() {
    return this._props.userStatus === TrmUserStatus.Denied;
  }
  public get isPending() {
    return this._props.userStatus === TrmUserStatus.Pending;
  }
  public get isUnverified() {
    return this._props.userStatus === TrmUserStatus.Unverified;
  }

  public isAnyUserStatus(...trmUserStatus: TrmUserStatus[]) {
    return trmUserStatus.includes(this._props.userStatus);
  }

  constructor(props: TrmUserProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        personId: this._props.personId,
        emailAddress: this._props.emailAddress,
        firstName: this._props.firstName,
        lastName: this._props.lastName,
        phoneNumber: this._props.phoneNumber,
        userStatus: this._props.userStatus,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTrmUserEntities = (state: AppState) => state.entityStores.trmUser.entities;

export const selectTrmUserById = (id: string) => (state: AppState) =>
  state.entityStores.trmUser.entities.get(id);

export const selectTrmUserMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.trmUser.meta.get(id);

export const selectTrmUserAndMetadataById = (id: string) =>
  createSelector(selectTrmUserById(id), selectTrmUserMetadataById(id), (trmUser, meta) => ({
    trmUser,
    meta,
  }));

export function selectTrmUserEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTrmUserEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TrmUserRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTrmUserEntitiesByPersonId(personId: string) {
  return createSelector(selectAllTrmUserEntities, (entities) =>
    entities.filter((e) => e.personId === personId).valueSeq(),
  );
}

export function selectTrmUserEntitiesByEmailAddress(emailAddress: string) {
  return createSelector(selectAllTrmUserEntities, (entities) =>
    entities.filter((e) => e.emailAddress === emailAddress).valueSeq(),
  );
}

export function selectTrmUserEntitiesByFirstName(firstName: string) {
  return createSelector(selectAllTrmUserEntities, (entities) =>
    entities.filter((e) => e.firstName === firstName).valueSeq(),
  );
}

export function selectTrmUserEntitiesByLastName(lastName: string) {
  return createSelector(selectAllTrmUserEntities, (entities) =>
    entities.filter((e) => e.lastName === lastName).valueSeq(),
  );
}

export function selectTrmUserEntitiesByPhoneNumber(phoneNumber: string) {
  return createSelector(selectAllTrmUserEntities, (entities) =>
    entities.filter((e) => e.phoneNumber === phoneNumber).valueSeq(),
  );
}

export function selectTrmUserEntitiesByUserStatus(userStatus: TrmUserStatus) {
  return createSelector(selectAllTrmUserEntities, (entities) =>
    entities.filter((e) => e.userStatus === userStatus).valueSeq(),
  );
}

export function selectTrmUserEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllTrmUserEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
